import {HttpService} from "../http/HttpService";
import {
    AppContext,
    AuthToken,
    BalanceAndBonus,
    LoginVerification,
    ManualLoginRequest,
    MultiFactorAuthProvider,
    MultiFactorAuthResponse,
    NextUpcomingCashback,
    RegistrationRequest,
    RegistrationResponse,
    RequestPrincipalVerification,
    ResetPasswordRequest,
    TokenLoginRequest,
    UserInfo,
    UserJourneyAndAttributes,
    UserLevelView,
    WidgetAuthentication
} from "../http/protocol";

export type ResetPasswordChannel = "email" | "sms";

export class UserService  {

    httpBackend : HttpService;

    constructor(httpBackend : HttpService) {
        this.httpBackend = httpBackend;
    }

    verifyChannelCode(userUid: string, code: string, link: boolean = false) : Promise<{}>{
        return this.httpBackend.get(`/user/verify/${userUid}/${code}/${link}`);
    }

    verifyLinkCode(userUid: string, code: string, link: boolean = true): Promise<UserInfo> {
        try {
            const response = this.httpBackend.get<UserInfo>(`/user/verify/${userUid}/${code}/${link}`);
            console.log(`Response from verifyLinkCode: ${response}`);
            return response;
        } catch (error) {
            console.error('Error in verifyLinkCode:', error);
            throw error;
        }
    }

    requestVerificationPrincipal(principal: string, verification: LoginVerification, token?: string): Promise<{}>
     {
        return this.httpBackend.post<{},RequestPrincipalVerification>("/user/request-principal-verification",
         { principal : principal, verification : verification },
          { "x-captcha-token"  : token });
    }

    verifyAccessToken(token:string): Promise<UserInfo> {
        return  this.httpBackend.post('/user/authenticate/facebook', {
            facebookToken : token
        });
    }
    logout() : Promise<{}>{
        return this.httpBackend.get("/user/logout");
    }
    updateUser(attributes : {[key:string] : string}) : Promise<UserInfo> {
        return this.httpBackend.post<UserInfo, {[key:string] : string}>("/user/update-attributes", attributes);
    }
    fetchUserInfo() : Promise<UserInfo> {
        return this.httpBackend.get("/user/details");
    }

    fetchBalances(currencies?: String[], skipBalance?: boolean) : Promise<BalanceAndBonus> {
        return this.httpBackend.post("/user/account-balances-and-bonuses", { currencies, skipBalance });
    }
    fetchUserJourney() : Promise<UserJourneyAndAttributes> {
        return this.httpBackend.get<UserJourneyAndAttributes>("/user/user-journey");
    }
    register(req: RegistrationRequest, token?: string): Promise<RegistrationResponse> {
        return this.httpBackend.post("/user/create-account", req, { "x-captcha-token"  : token });
    }
    login(req: ManualLoginRequest, token?: string, appContext?: AppContext): Promise<UserInfo> {
        return this.httpBackend.post("/user/authenticate", req, { "x-captcha-token"  : token, "x-preferred-app-context": appContext });
    }
    loginWithToken(req: TokenLoginRequest): Promise<UserInfo> {
        return this.httpBackend.post("/user/authenticate/token", req);
    }
    facebookLogin(facebookToken : string) : Promise<UserInfo> {
        return this.httpBackend.post("/user/authenticate/facebook", { facebookToken : facebookToken })
    }

    requestResetPassword(address: string, channel: ResetPasswordChannel, token?: string): Promise<{}> {
        return this.httpBackend.get(`/user/recover-password/${channel}/${encodeURIComponent(address)}`, { "x-captcha-token"  : token });
    }

    resetPassword(request : ResetPasswordRequest) : Promise<{}>{
        return this.httpBackend.post("/user/password/reset", request);
    }

    updateUserSettings(key:string, value:string) : Promise<{}>{
        return this.httpBackend.put(`/user/settings/${key}/${value}`);
    }

    getUserSettings(key:string) : Promise<string> {
        return this.httpBackend.get(`/user/settings/${key}`);
    }

    getAllUserSettings() : Promise<{ [key: string]: string; }> {
        return this.httpBackend.get(`/user/settings`);
    }

    setKycStarted(): Promise<{}> {
        return this.httpBackend.get(`/user/kyc-started`);
    }

    getUserLoginTokens(): Promise<AuthToken[]> {
        return this.httpBackend.get('/token/all');
    }

    getTokenByRefId(id:number): Promise<AuthToken> {
        return this.httpBackend.get("/token/token/"+id)
    }

    clearUserLoginToken(id:number): Promise<{}> {
        return this.httpBackend.get(`/token/token/delete/${id}`);
    }

    clearAllUserLoginTokens(): Promise<{}> {
        return this.httpBackend.get('/token/all/clear');
    }
    enableMfa(provider : MultiFactorAuthProvider)  : Promise<MultiFactorAuthResponse> {
        return this.httpBackend.post("/user/mfa/enable", { provider : provider })
    }
    verifyMfa(provider : MultiFactorAuthProvider, code : string) : Promise<{}> {
        return this.httpBackend.post("/user/mfa/verify", { provider : provider, verificationCode : code});
    }
    fetchUserLevelView(): Promise<UserLevelView> {
        return this.httpBackend.get('/user/level');
    }
    widgetsAuthenticate() : Promise<WidgetAuthentication[]> {
        return this.httpBackend.get("/user/widget-authentication");
    }

    async affiliateTrackerClicked(tracker: string):Promise<{}> {
        return this.httpBackend.get('/user/affiliate/' + tracker);
    }

    async updateUserLocale(locale: string) {
        return this.httpBackend.post(`/user/locale/${locale}`, null)
    }

    async validateMayaSessionId(sessionId: string): Promise<UserInfo> {
        return this.httpBackend.post(`/user/authenticate/mayaminiapp`, {sessionId})
    }

    async getNextUpcomingCashbackForUser(): Promise<NextUpcomingCashback[]> {
        return this.httpBackend.get(`/user/upcoming-cashbacks`);
    }

    // Not user specific ( to have cashback counter for unAuth users )
    async getNextUpcomingCashbackForPublic(): Promise<NextUpcomingCashback[]> {
        return this.httpBackend.get(`/user/upcoming-cashback-time`);
    }

    async sendGlifeAuthCode(authCode: string, deviceId: string): Promise<UserInfo> {
        return this.httpBackend.post(`/user/authenticate/glife`, {authCode, deviceId});
    }

    async getOneTimeToken(): Promise<{ token: string }> {
        return this.httpBackend.get(`/user/glife/one-time-token`);
    }

    async authenticeWithOneTimeToken(token: string): Promise<UserInfo> {
        return this.httpBackend.post(`/user/authenticate/glife/one-time-token`, { token });
    }
}

